import type {
  Grid,
  GridPosition,
  ReelColumn,
  ReelIndex,
  RowIndex,
} from '../core/types';
import type { WildMultiplierPosition } from '../core/wins';

function isReelIndex(value: number): value is ReelIndex {
  return Number.isSafeInteger(value) && value >= 0 && value <= 4;
}

function isRowIndex(value: number): value is RowIndex {
  return Number.isSafeInteger(value) && value >= 0 && value <= 2;
}

function positionKey(position: GridPosition): string {
  return `${position.reel}:${position.row}`;
}

export function normalizeStickyWilds(positions: readonly GridPosition[]): readonly WildMultiplierPosition[] {
  const unique = new Map<string, WildMultiplierPosition>();
  positions.forEach((position) => {
    if (!isReelIndex(position.reel) || !isRowIndex(position.row)) {
      throw new RangeError('Sticky-Wild-Position liegt außerhalb des 5×3-Rasters.');
    }
    const multiplier = 'multiplier' in position ? Number(position.multiplier) : 1;
    if (!Number.isSafeInteger(multiplier) || multiplier < 1) {
      throw new RangeError('Sticky-Wild-Multiplikatoren müssen positive sichere Ganzzahlen sein.');
    }
    const key = positionKey(position);
    const previous = unique.get(key);
    const normalizedMultiplier = Math.max(previous?.multiplier ?? 1, multiplier);
    unique.set(key, Object.freeze({
      reel: position.reel,
      row: position.row,
      ...(normalizedMultiplier === 1 ? {} : { multiplier: normalizedMultiplier }),
    }));
  });
  return Object.freeze([...unique.values()].sort(
    (left, right) => left.reel - right.reel || left.row - right.row,
  ));
}

export function hasStickyWild(
  positions: readonly GridPosition[],
  reel: number,
  row: number,
): boolean {
  return positions.some((position) => position.reel === reel && position.row === row);
}

export function mergeStickyWilds(
  current: readonly GridPosition[],
  additions: readonly GridPosition[],
): readonly WildMultiplierPosition[] {
  return normalizeStickyWilds([...current, ...additions]);
}

export function applyStickyWilds(grid: Grid, positions: readonly GridPosition[]): Grid {
  const normalized = normalizeStickyWilds(positions);
  const columns = grid.map((column, reel) => Object.freeze(column.map((symbol, row) => (
    hasStickyWild(normalized, reel, row) ? 'WILD' : symbol
  ))) as ReelColumn);
  return Object.freeze(columns) as unknown as Grid;
}

export function collectNewStickyWilds(
  gridAfterExistingStickies: Grid,
  existing: readonly GridPosition[],
): readonly WildMultiplierPosition[] {
  const additions: GridPosition[] = [];
  gridAfterExistingStickies.forEach((column, reel) => {
    column.forEach((symbol, row) => {
      if (symbol !== 'WILD' || hasStickyWild(existing, reel, row)) return;
      additions.push(Object.freeze({ reel: reel as ReelIndex, row: row as RowIndex }));
    });
  });
  return normalizeStickyWilds(additions);
}
